import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Table, Button, Checkbox, message, Popconfirm, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';

const TrabajadoresModal = ({ open, onClose, onUpdate }) => {
    const [trabajadores, setTrabajadores] = useState([]);
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nuevoNombre, setNuevoNombre] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingNombre, setEditingNombre] = useState('');

    const token = localStorage.getItem('token');
    const config = {
        headers: { Authorization: `Bearer ${token}` }
    };

    // Cargar trabajadores y obras
    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [trabResponse, obrasResponse] = await Promise.all([
                axios.get('/api/v1/trabajadores', config),
                axios.get('/api/obras', config)
            ]);
            setTrabajadores(trabResponse.data);
            setObras(obrasResponse.data);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            message.error('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            cargarDatos();
        }
    }, [open]);

    // Crear nuevo trabajador
    const handleCrear = async () => {
        if (!nuevoNombre.trim()) {
            message.warning('Ingresa un nombre');
            return;
        }

        try {
            await axios.post('/api/v1/trabajadores', { nombre: nuevoNombre }, config);
            message.success('Trabajador creado');
            setNuevoNombre('');
            cargarDatos();
            onUpdate?.();
        } catch (error) {
            console.error('Error al crear trabajador:', error);
            message.error('Error al crear el trabajador');
        }
    };

    // Actualizar nombre
    const handleActualizar = async (id) => {
        if (!editingNombre.trim()) {
            message.warning('Ingresa un nombre');
            return;
        }

        try {
            await axios.put(`/api/v1/trabajadores/${id}`, { nombre: editingNombre }, config);
            message.success('Trabajador actualizado');
            setEditingId(null);
            setEditingNombre('');
            cargarDatos();
            onUpdate?.();
        } catch (error) {
            console.error('Error al actualizar trabajador:', error);
            message.error('Error al actualizar el trabajador');
        }
    };

    // Eliminar trabajador
    const handleEliminar = async (id) => {
        try {
            await axios.delete(`/api/v1/trabajadores/${id}`, config);
            message.success('Trabajador eliminado');
            cargarDatos();
            onUpdate?.();
        } catch (error) {
            console.error('Error al eliminar trabajador:', error);
            message.error('Error al eliminar el trabajador');
        }
    };

    // Asignar/desasignar obra
    const handleToggleObra = async (trabajadorId, obraId, asignada) => {
        const trabajador = trabajadores.find(t => t.id === trabajadorId);
        if (!trabajador) return;

        let obrasIds = trabajador.obras_ids ? trabajador.obras_ids.split(',').map(Number) : [];

        if (asignada) {
            obrasIds = obrasIds.filter(id => id !== obraId);
        } else {
            obrasIds.push(obraId);
        }

        try {
            await axios.put(`/api/v1/trabajadores/${trabajadorId}/obras`, { obras_ids: obrasIds }, config);
            cargarDatos();
            onUpdate?.();
        } catch (error) {
            console.error('Error al asignar obras:', error);
            message.error('Error al asignar la obra');
        }
    };

    // Columnas de obras dinámicas
    const obrasColumns = obras.map(obra => ({
        title: obra.nombre_obra,
        key: `obra_${obra.id}`,
        width: 100,
        align: 'center',
        render: (_, record) => {
            const obrasIds = record.obras_ids ? record.obras_ids.split(',') : [];
            const asignada = obrasIds.includes(String(obra.id));
            return (
                <Checkbox
                    checked={asignada}
                    onChange={() => handleToggleObra(record.id, obra.id, asignada)}
                />
            );
        }
    }));

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
            key: 'nombre',
            width: 200,
            render: (text, record) => {
                if (editingId === record.id) {
                    return (
                        <Space>
                            <Input
                                value={editingNombre}
                                onChange={(e) => setEditingNombre(e.target.value)}
                                onPressEnter={() => handleActualizar(record.id)}
                                style={{ width: 150 }}
                            />
                            <Button
                                type="primary"
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={() => handleActualizar(record.id)}
                            />
                        </Space>
                    );
                }
                return <strong>{text}</strong>;
            }
        },
        ...obrasColumns,
        {
            title: 'Acciones',
            key: 'acciones',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingId(record.id);
                            setEditingNombre(record.nombre);
                        }}
                    />
                    <Popconfirm
                        title="¿Eliminar este trabajador?"
                        onConfirm={() => handleEliminar(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Modal
            title="Gestionar Trabajadores"
            open={open}
            onCancel={onClose}
            footer={null}
            width={800}
        >
            {/* Formulario para nuevo trabajador */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <Input
                    placeholder="Nombre del trabajador"
                    value={nuevoNombre}
                    onChange={(e) => setNuevoNombre(e.target.value)}
                    onPressEnter={handleCrear}
                    style={{ flex: 1 }}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCrear}>
                    Agregar
                </Button>
            </div>

            {/* Tabla de trabajadores */}
            <Table
                columns={columns}
                dataSource={trabajadores.map(t => ({ ...t, key: t.id }))}
                loading={loading}
                pagination={false}
                scroll={{ x: 600, y: 400 }}
                size="small"
            />
        </Modal>
    );
};

export default TrabajadoresModal;
